import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

import api from "../../Utils/api";
import Select from "react-select";

export default function AddUserModal({ locations, onClose }) {
  const user = useSelector((state) => state.user.user);
  const pronouns = ["She/Her", "He/Him", "They/Them", "Prefer not to say"];
  const genders = [
    "Female",
    "Male",
    "Non-binary",
    "Other",
    "Prefer not to say",
  ];
  const accessLevel = ["Admin", "employee"];

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    preferredName: "",
    email: "",
    birthday: "",
    pronouns: "",
    gender: "",
    phone: "",
    password: "",
    passwordConfirm: "",
    deputyAccessLevel: "employee",
    companyID: "",
    locations: {
      primaryLocation: {},
      subLocations: [],
    },
  });

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);

  useEffect(() => {
    if (user && user.deputyAccessLevel === "system_administrator") {
      fetchCompanies();
    } else if (user && user.deputyAccessLevel === "company_manager") {
      // Auto-set company for company managers
      setFormData((prev) => ({ ...prev, companyID: user.companyID }));
    }
  }, [user]);

  const fetchCompanies = async () => {
    try {
      const response = await api.get("/company");
      if (response.data.success) {
        // Only show active (non-archived) companies
        setCompanies((response.data.companies || []).filter(company => !company.archive));
      }
    } catch (error) {
      console.error("Failed to fetch companies:", error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const [selectedMainLocation, setSelectedMainLocation] = useState(null);
  const changeMainLocation = (option) => {
    // //console.log(option,'options');
    setSelectedMainLocation(option);

    if (option) {
      setFormData({
        ...formData,
        locations: {
          ...formData.locations,
          primaryLocation: {
            _id: option._id,
            name: option.name,
          },
        },
      });
    } else {
      setFormData({
        ...formData,
        locations: {
          ...formData.locations,
          primaryLocation: {},
        },
      });
    }
  };

  const [selectedSubLocations, setSelectedSubLocations] = useState([]);
  const changeSubLocations = (option) => {
    setSelectedSubLocations(option);
    if (option) {
      const subLocations = option.map((loc) => {
        return {
          name: loc.name,
          _id: loc._id,
        };
      });

      setFormData({
        ...formData,
        locations: {
          ...formData.locations,
          subLocations: subLocations,
        },
      });
    } else {
      setFormData({
        ...formData,
        locations: {
          ...formData.locations,
          subLocations: {},
        },
      });
    }
  };

  const addNewUser = async () => {
    // prevent empty fields
    const {
      firstName,
      lastName,
      preferredName,
      email,
      birthday,
      pronouns,
      gender,
      phone,
      password,
      passwordConfirm,
      deputyAccessLevel,
      locations,
    } = formData;

    if (
      !firstName ||
      !lastName ||
      !preferredName ||
      !email ||
      !phone ||
      !locations.primaryLocation._id ||
      !deputyAccessLevel ||
      !password
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (password !== passwordConfirm) {
      toast.error("Passwords do not match");
      return;
    }

    // Example API call
    try {
      const res = await api.post("/user/add", {
        firstName,
        lastName,
        preferredName,
        email,
        birthday,
        pronouns,
        gender,
        phone,
        password,
        deputyAccessLevel,
        companyID: formData.companyID || null,
        locations,
      });

      if (res.data.success) {
        toast.success("Added successfully!");
        closeModal();
      } else {
        toast.error(res.data.message || "failed!");
      }
    } catch (error) {
      toast.error("Server error, please try again later");
      console.error(error);
    }
  };

  const closeModal = () => {
    //clear formdata

    setFormData({
      firstName: "",
      lastName: "",
      preferredName: "",
      email: "",
      birthday: "",
      pronouns: "",
      gender: "",
      phone: "",
      password: "",
      passwordConfirm: "",
      deputyAccessLevel: "employee",
      companyID: "",
      locations: {
        primaryLocation: {},
        subLocations: [],
      },
    });
    setSelectedCompany(null);
    setSelectedMainLocation(null);
    setSelectedSubLocations([]);
    // close modal

    onClose();
  };

  return (
    <div className="fixed inset-0 p-4 flex flex-wrap justify-center items-center w-full h-full z-[1000] before:fixed before:inset-0 before:w-full before:h-full before:bg-[rgba(0,0,0,0.5)] overflow-auto">
      <div className="w-[600px] bg-white shadow-lg rounded-lg p-6 relative">
        <div className="flex items-center pb-3 border-b border-gray-300">
          <h3 className="text-slate-900 text-2xl font-semibold flex-1 text-left">
            Add New Staff
          </h3>
          <svg
            id="closeIcon"
            xmlns="http://www.w3.org/2000/svg"
            className="w-3.5 h-3.5 ml-2 cursor-pointer shrink-0 fill-gray-400 hover:fill-red-500"
            viewBox="0 0 320.591 320.591"
            onClick={() => closeModal()}
          >
            <path
              d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z"
              data-original="#000000"
            />
            <path
              d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z"
              data-original="#000000"
            />
          </svg>
        </div>
        <div className="my-6">
          <ul className="space-y-3">
            {/* Username */}
            <li className="flex gap-6">
              <div className="w-1/3 flex items-center">
                <label
                  htmlFor="preferredName"
                  className="block text-sm font-medium text-gray-700"
                >
                  Username (Preferred Name){" "}
                  <span className="text-red-500">*</span>
                </label>
              </div>
              <div className="w-2/3">
                <input
                  type="text"
                  id="preferredName"
                  name="preferredName"
                  value={formData.preferredName}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-4 py-2 focus:border-gray-500 focus:ring-gray-500"
                  required
                  placeholder="Your preferred display name"
                />
              </div>
            </li>

            {/* Name Fields */}
            <li className="flex gap-6">
              <div className="w-1/3 flex items-center">
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium text-gray-700"
                >
                  First Name <span className="text-red-500">*</span>
                </label>
              </div>
              <div className="w-2/3">
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className="w-full mt-1 block w-full rounded-md border border-gray-300 px-4 py-2 focus:border-gray-500 focus:ring-gray-500"
                  required
                />
              </div>
            </li>

            {/* lastname */}
            <li className="flex gap-6">
              <div className="w-1/3 flex items-center">
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium text-gray-700"
                >
                  Last Name <span className="text-red-500">*</span>
                </label>
              </div>
              <div className="w-2/3">
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="w-full mt-1 block w-full rounded-md border border-gray-300 px-4 py-2 focus:border-gray-500 focus:ring-gray-500"
                  required
                />
              </div>
            </li>

            {/* Email */}
            <li className="flex gap-6">
              <div className="w-1/3 flex items-center">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email Address <span className="text-red-500">*</span>
                </label>
              </div>
              <div className="w-2/3">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-4 py-2 focus:border-gray-500 focus:ring-gray-500"
                  required
                  placeholder="you@example.com"
                />
              </div>
            </li>

            {/* Phone */}
            <li className="flex gap-6">
              <div className="w-1/3 flex items-center">
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-700"
                >
                  Phone Number<span className="text-red-500">*</span>
                </label>
              </div>
              <div className="w-2/3">
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-4 py-2 focus:border-gray-500 focus:ring-gray-500"
                  placeholder="+1 555 123 4567"
                  pattern="^\+?[0-9\s\-]{7,15}$"
                  title="Enter a valid phone number"
                />
              </div>
            </li>

            {/* Main Location */}
            <li className="flex gap-6">
              <div className="w-1/3 flex items-center">
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-700"
                >
                  Main Location<span className="text-red-500">*</span>
                </label>
              </div>
              <div className="w-2/3">
                <Select
                  name="Location List"
                  placeholder={"Select a Location"}
                  className={`basic-multi-select w-full cursor-pointer !m-0 !p-0`}
                  classNamePrefix="select"
                  options={locations}
                  value={selectedMainLocation}
                  onChange={changeMainLocation}
                  getOptionLabel={(option) => option.name} // Show only the label
                  getOptionValue={(option) => option._id}
                  isClearable={true}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      borderColor: "bg-gray-500", // Change border color based on focus state
                      boxShadow: "none", // Remove default shadow
                      "&:hover": {
                        borderColor: "#3f1fbd", // Change border color on hover
                      },
                    }),
                  }}
                />
              </div>
            </li>

            {/* Other Location */}
            <li className="flex gap-6">
              <div className="w-1/3 flex items-center">
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-700"
                >
                  Other Locations
                </label>
              </div>
              <div className="w-2/3">
                <Select
                  isMulti="true"
                  name="Location List"
                  placeholder={"Select a Location"}
                  className={`basic-multi-select w-full cursor-pointer !m-0 !p-0`}
                  classNamePrefix="select"
                  options={locations}
                  value={selectedSubLocations}
                  onChange={changeSubLocations}
                  getOptionLabel={(option) => option.name} // Show only the label
                  getOptionValue={(option) => option._id}
                  isClearable={true}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      borderColor: "bg-gray-500", // Change border color based on focus state
                      boxShadow: "none", // Remove default shadow
                      "&:hover": {
                        borderColor: "#3f1fbd", // Change border color on hover
                      },
                    }),
                  }}
                />
              </div>
            </li>

            {/* Birthday, Pronoun, Gender */}
            <li className="flex gap-6">
              <div className="w-1/3 flex items-center">
                <label
                  htmlFor="birthday"
                  className="block text-sm font-medium text-gray-700"
                >
                  Birthday
                </label>
              </div>
              <div className="w-2/3">
                <input
                  type="date"
                  id="birthday"
                  name="birthday"
                  value={formData.birthday}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-4 py-2 focus:border-gray-500 focus:ring-gray-500"
                  max={new Date().toISOString().split("T")[0]}
                />
              </div>
            </li>
            <li className="flex gap-6">
              <div className="w-1/3 flex items-center">
                <label
                  htmlFor="pronouns"
                  className="block text-sm font-medium text-gray-700"
                >
                  Pronouns
                </label>
              </div>
              <div className="w-2/3">
                <select
                  id="pronouns"
                  name="pronouns"
                  value={formData.pronouns}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-4 py-2 focus:border-gray-500 focus:ring-gray-500"
                >
                  <option value="">Select pronouns</option>
                  {pronouns.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
            </li>
            <li className="flex gap-6">
              <div className="w-1/3 flex items-center">
                <label
                  htmlFor="gender"
                  className="block text-sm font-medium text-gray-700"
                >
                  Gender
                </label>
              </div>
              <div className="w-2/3">
                <select
                  id="gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-4 py-2 focus:border-gray-500 focus:ring-gray-500"
                >
                  <option value="">Select gender</option>
                  {genders.map((g) => (
                    <option key={g} value={g}>
                      {g}
                    </option>
                  ))}
                </select>
              </div>
            </li>

            {/* Company - Only for system administrators */}
            {user && user.deputyAccessLevel === "system_administrator" && (
              <li className="flex gap-6">
                <div className="w-1/3 flex items-center">
                  <label
                    htmlFor="company"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Company
                  </label>
                </div>
                <div className="w-2/3">
                  <Select
                    name="company"
                    placeholder="Select a Company"
                    className="basic-multi-select w-full cursor-pointer !m-0 !p-0"
                    classNamePrefix="select"
                    options={companies}
                    value={selectedCompany}
                    onChange={(option) => {
                      setSelectedCompany(option);
                      setFormData((prev) => ({
                        ...prev,
                        companyID: option ? option._id : "",
                      }));
                    }}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option._id}
                    isClearable={true}
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderColor: "bg-gray-500",
                        boxShadow: "none",
                        "&:hover": {
                          borderColor: "#3f1fbd",
                        },
                      }),
                    }}
                  />
                </div>
              </li>
            )}

            {/* employee */}
            <li className="flex gap-6">
              <div className="w-1/3 flex items-center">
                <label
                  htmlFor="access"
                  className="block text-sm font-medium text-gray-700"
                >
                  Access level<span className="text-red-500">*</span>
                </label>
              </div>
              <div className="w-2/3">
                <select
                  id="access"
                  name="deputyAccessLevel"
                  value={formData.deputyAccessLevel}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-4 py-2 focus:border-gray-500 focus:ring-gray-500"
                  disabled
                >
                  <option value="">Select Access Level</option>
                  {accessLevel.map((g) => (
                    <option key={g} value={g}>
                      {g}
                    </option>
                  ))}
                </select>
              </div>
            </li>

            {/* Pawword */}
            <li className="flex gap-6">
              <div className="w-1/3 flex items-center">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700"
                >
                  Password <span className="text-red-500">*</span>
                </label>
              </div>
              <div className="w-2/3">
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-4 py-2 focus:border-gray-500 focus:ring-gray-500"
                  required
                  minLength={6}
                  placeholder="At least 6 characters"
                />
              </div>
            </li>

            {/* Confirm Pawword */}
            <li className="flex gap-6">
              <div className="w-1/3 flex items-center">
                <label
                  htmlFor="passwordConfirm"
                  className="block text-sm font-medium text-gray-700"
                >
                  Confirm Password <span className="text-red-500">*</span>
                </label>
              </div>
              <div className="w-2/3">
                <input
                  type="password"
                  id="passwordConfirm"
                  name="passwordConfirm"
                  value={formData.passwordConfirm}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-4 py-2 focus:border-gray-500 focus:ring-gray-500"
                  required
                  minLength={6}
                  placeholder="Re-enter your password"
                />
              </div>
            </li>
          </ul>
        </div>
        {/* Footer buttons */}
        <div className="flex justify-end gap-2">
          <button
            onClick={() => closeModal()}
            className="bg-gray-100 text-gray-800 px-4 py-1.5 rounded-md hover:bg-gray-200"
          >
            Close
          </button>
          <button
            onClick={() => addNewUser()}
            className="bg-[#0a2449] text-white px-4 py-1.5 rounded-md hover:bg-gray-700"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
